import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, Observable, takeUntil, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { ApiErrorCodes } from '../shared/constants/api-error-codes';
import { AuthService } from '../services/auth/auth.service';
import { MessageModalService } from '../services/message-modal/message-modal.service';
import { environment } from '../../environments/environment';

export const TokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<any> => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const messageModal = inject(MessageModalService);

  const token = authService.accessToken;

  // Cookie-based auth requires HTTPS (the session cookie is marked Secure). On
  // environments served over plain HTTP, fall back to sending the token as a
  // bearer header, which the backend still supports. But only if this is a backend request (ie, not a GCS upload or other domain)

  const useBearerToken = req.url.startsWith(environment.apiBaseUrl) && environment.apiBaseUrl.startsWith('http://');

  const authReq =
    useBearerToken && token
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` }, withCredentials: false })
      : req;

  const request$ = next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        authService.logout().subscribe({ complete: () => router.navigate(['/login']) });
      }
      if (error.status === 403) {
        const errorCode: string | undefined = error.error?.error_code;
        if (errorCode === ApiErrorCodes.AUTH_EULA_ACCEPTANCE_REQUIRED) {
          // Handled centrally by redirecting; swallow the generic "failed to load"
          // modal that component error handlers would otherwise show on top.
          messageModal.suppressErrorModals();
          router.navigate(['/license-agreement']);
        }
      }
      return throwError(() => error);
    })
  );

  // All requests are subject to being cancelled on logout, other than requests to logout itself, or login
  if (req.url.includes('/logout') || req.url.includes('/login')) {
    return request$;
  } else {
    return request$.pipe(takeUntil(authService.logoutSignal$));
  }
};
